/**
 * UsdtRatesEditor.js — Editor de "Zona de Cambios" (Iter 62 · ADR-010).
 *
 * Schema único (`usdt_rates_config`):
 *   - tasa_compra_usdt  → UYU que paga el CEO por 1 USDT al proveedor (global)
 *   - corredores.{CUP,USD,MLC}.tasa_cliente     → 1 UYU = X destino (cliente)
 *   - corredores.{CUP,USD,MLC}.precio_proveedor → 1 USDT = X destino
 *   - corredores.{CUP,USD,MLC}.ajuste_operativo → USDT extra (fees/slippage)
 *
 * BANDEC quedó unificado dentro de "USD Clásica" (es el mismo corredor — moneda
 * USD). Ya no se muestra como corredor independiente.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CORREDORES = [
  { key: 'CUP', label: 'CUP',          help: 'Pesos cubanos (transferencia)' },
  { key: 'USD', label: 'USD Clásica',  help: 'Tarjeta USD clásica · incluye BANDEC Prepago' },
  { key: 'MLC', label: 'MLC',          help: 'Moneda libremente convertible' },
];

const FIELD_LABEL = {
  tasa_cliente:     '1 UYU = ? destino',
  precio_proveedor: '1 USDT = ? destino',
  ajuste_operativo: 'Ajuste operativo (USDT)',
};
const FIELD_HELP = {
  tasa_cliente:     'Lo que recibe el beneficiario por cada 1 UYU enviado.',
  precio_proveedor: 'Cuánto destino entrega el proveedor por cada 1 USDT.',
  ajuste_operativo: 'USDT extra que se suman al consumido (fees red, slippage).',
};

const UsdtRatesEditor = () => {
  const { getAuthHeaders, user } = useAuth();
  const canEdit = user?.role === 'admin' ||
    (user?.role === 'payment_agent' && (user?.country || '').toUpperCase() === 'CU');

  const [cfg, setCfg] = useState(null);
  const [draftCore, setDraftCore] = useState(null);          // { tasa_compra_usdt }
  const [draftCors, setDraftCors] = useState({});            // { CUP: {tasa_cliente, ...}, ... }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/usdt-config`, { headers: getAuthHeaders() });
        setCfg(r.data);
        setDraftCore(null);
        setDraftCors({});
      } catch {
        toast.error('No se pudo cargar la configuración USDT');
      }
    })();
  }, [getAuthHeaders]);

  const dirty = useMemo(() => {
    if (draftCore !== null) return true;
    return Object.keys(draftCors).length > 0;
  }, [draftCore, draftCors]);

  const val = (ccy, field) => {
    const draftRow = draftCors[ccy] || {};
    if (field in draftRow) return draftRow[field];
    return cfg?.corredores?.[ccy]?.[field] ?? '';
  };

  const setCorVal = (ccy, field, raw) => {
    setDraftCors((prev) => ({
      ...prev,
      [ccy]: {
        ...(prev[ccy] || {}),
        [field]: raw === '' ? '' : parseFloat(raw),
      },
    }));
  };

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const payload = {};
      if (draftCore !== null && draftCore !== '') {
        payload.tasa_compra_usdt = parseFloat(draftCore);
      }
      if (Object.keys(draftCors).length) {
        const out = {};
        for (const [ccy, patch] of Object.entries(draftCors)) {
          const clean = {};
          for (const [k, v] of Object.entries(patch)) {
            if (v === '' || v === null || v === undefined) continue;
            clean[k] = parseFloat(v);
          }
          if (Object.keys(clean).length) out[ccy] = clean;
        }
        if (Object.keys(out).length) payload.corredores = out;
      }
      const r = await axios.put(`${API}/usdt-config`, payload, { headers: getAuthHeaders() });
      setCfg(r.data);
      setDraftCore(null);
      setDraftCors({});
      toast.success('Tasas actualizadas');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  const tasaCompra = draftCore !== null && draftCore !== '' ? draftCore : (cfg.tasa_compra_usdt ?? '');

  return (
    <Card className="p-5 border-0 shadow-sm" data-testid="usdt-rates-editor">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Zona de Cambios · Tasas activas</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Fuente única de verdad para todo el motor de remesas. La ganancia total
            (en USDT) se reparte <b>50% Proveedor</b> · <b>25% CEO</b> · <b>25% Gestor</b>.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase text-slate-500">Compra USDT</p>
          <p className="text-emerald-600 font-bold font-mono">
            {Number(tasaCompra || 0).toLocaleString('es-UY', { maximumFractionDigits: 4 })} UYU
          </p>
        </div>
      </div>

      {/* GLOBAL: Tasa de compra USDT */}
      <div className="bg-slate-50/80 rounded-lg p-4 border border-slate-200 mb-5">
        <label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
          Tasa de compra USDT · UYU por 1 USDT
        </label>
        <Input
          type="number"
          step="any"
          min="0"
          value={tasaCompra}
          onChange={(e) => setDraftCore(e.target.value === '' ? '' : e.target.value)}
          disabled={!canEdit}
          className="h-10 font-mono mt-1"
          data-testid="usdt-input-tasa_compra_usdt"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          UYU que el CEO le paga al proveedor por cada 1 USDT comprado. Aplica
          globalmente a todos los corredores.
        </p>
      </div>

      {/* CORREDORES */}
      <div className="space-y-3">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
          Corredores destino
        </p>
        {CORREDORES.map(({ key, label, help }) => (
          <div
            key={key}
            className="bg-emerald-50/40 rounded-lg p-4 border border-emerald-100"
            data-testid={`corredor-${key.toLowerCase()}`}
          >
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <p className="text-base font-bold text-slate-900">{label}</p>
                <p className="text-[11px] text-slate-500">{help}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {['tasa_cliente', 'precio_proveedor', 'ajuste_operativo'].map((f) => (
                <div key={f}>
                  <label className="text-[10px] uppercase tracking-wide text-emerald-800 font-bold">
                    {FIELD_LABEL[f]}
                  </label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={val(key, f)}
                    onChange={(e) => setCorVal(key, f, e.target.value)}
                    disabled={!canEdit}
                    className="h-10 font-mono"
                    data-testid={`usdt-input-${key.toLowerCase()}-${f}`}
                  />
                  <p className="text-[10px] text-slate-500 mt-1">{FIELD_HELP[f]}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="flex justify-end mt-4">
          <Button
            disabled={saving || !dirty}
            onClick={handleSave}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="usdt-save-btn"
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      )}
      {cfg.updated_at && (
        <p className="text-[10px] text-slate-400 mt-3">
          Última actualización: {new Date(cfg.updated_at).toLocaleString('es-ES')}
        </p>
      )}
    </Card>
  );
};

export default UsdtRatesEditor;
